
import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { LogIn, ChefHat, Menu, X, Ticket, Briefcase, Handshake, UserPlus } from 'lucide-react';
import Button from '@/components/ui-elements/button';
import { useTranslationUtils } from '@/hooks/useTranslationUtils';
import LanguageSwitcher from './language-switcher';
import { useIsMobile } from '@/hooks/use-mobile';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/context/AuthContext';

const Navbar = () => {
  const navigate = useNavigate();
  const { t } = useTranslationUtils();
  const isMobile = useIsMobile();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [appName, setAppName] = useState('KFC Antilles');
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const { user } = useAuth();

  useEffect(() => {
    fetchAppSettings();
  }, []);

  const fetchAppSettings = async () => {
    try {
      const { data, error } = await supabase.from('app_settings').select('*').single();
      if (error) {
        console.error('Error fetching app settings:', error);
        return;
      }
      if (data) {
        if (data.app_name) setAppName(data.app_name);
        if (data.logo_url) setLogoUrl(data.logo_url);
      }
    } catch (error) {
      console.error('Error fetching app settings:', error);
    }
  };

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  const handleLoginClick = () => {
    console.log('Navigating to login from navbar');
    navigate('/login', {
      state: {
        isSignUp: false
      }
    });
    setIsMenuOpen(false);
  };

  const handleSignUpClick = () => {
    console.log('Navigating to sign up from navbar');
    navigate('/login', {
      state: {
        isSignUp: true
      }
    });
    setIsMenuOpen(false);
  };

  const handleLogoClick = () => {
    if (user) {
      navigate('/dashboard');
    } else {
      navigate('/');
    }
  };

  return (
    <header className="bg-[#E4002B] text-white shadow-md relative z-50">
      <div className="container mx-auto flex justify-between items-center py-4 px-4 my-0">
        <div className="flex items-center gap-3 cursor-pointer" onClick={handleLogoClick}>
          {logoUrl ? <img src={logoUrl} alt={t('common:logo')} className="h-12 w-12 object-contain" /> : <ChefHat className="h-8 w-8" />}
          <h1 className="text-lg sm:text-xl font-bold truncate max-w-[150px] sm:max-w-none">{appName}</h1>
        </div>

        {isMobile ? (
          <>
            <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" onClick={toggleMenu}>
              {isMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </Button>
            
            {isMenuOpen && (
              <div className="absolute top-[100%] left-0 right-0 bg-[#E4002B] flex flex-col items-center shadow-md z-50 py-[15px]">
                <div className="flex flex-col items-center gap-4 w-full px-4">
                  <Link to="/festi-pass" className="w-full" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e] w-full justify-start" leftIcon={<Ticket className="h-4 w-4" />}>
                      Festi-Pass
                    </Button>
                  </Link>
                  <Link to="/applications" className="w-full" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e] w-full justify-start" leftIcon={<Briefcase className="h-4 w-4" />}>
                      {t('navigation:applications')}
                    </Button>
                  </Link>
                  <Link to="/partner" className="w-full" onClick={() => setIsMenuOpen(false)}>
                    <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e] w-full justify-start" leftIcon={<Handshake className="h-4 w-4" />}>
                      {t('navigation:partner')}
                    </Button>
                  </Link>
                  <LanguageSwitcher variant="ghost" className="w-full" aria-label={t('language:changeLanguage')} />
                  <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e] w-full justify-center" rightIcon={<LogIn className="h-4 w-4" />} onClick={handleLoginClick}>
                    {t('auth:signIn')}
                  </Button>
                  <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e] w-full justify-center" rightIcon={<UserPlus className="h-4 w-4" />} onClick={handleSignUpClick}>
                    {t('auth:signUp')}
                  </Button>
                </div>
              </div>
            )}
          </>
        ) : (
          <div className="flex items-center gap-2 mx-[21px]">
            <Link to="/festi-pass">
              <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" leftIcon={<Ticket className="h-4 w-4" />}>
                Festi-Pass
              </Button>
            </Link>
            <Link to="/applications">
              <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" leftIcon={<Briefcase className="h-4 w-4" />}>
                {t('navigation:applications')}
              </Button>
            </Link>
            <Link to="/partner">
              <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" leftIcon={<Handshake className="h-4 w-4" />}>
                {t('navigation:partner')}
              </Button>
            </Link>
            <LanguageSwitcher variant="ghost" aria-label={t('language:changeLanguage')} />
            <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" rightIcon={<LogIn className="h-4 w-4" />} onClick={handleLoginClick}>
              {t('auth:signIn')}
            </Button>
            <Button variant="ghost" size="sm" className="text-white hover:bg-[#c7001e]" rightIcon={<UserPlus className="h-4 w-4" />} onClick={handleSignUpClick}>
              {t('auth:signUp')}
            </Button>
          </div>
        )}
      </div>
    </header>
  );
};

export default Navbar;
